import api from '../api/axiosInstance';

export interface DeployNodePayload {
  gnbNodeId: string;
  username?: string;
  password?: string;
  isNewNode: boolean;
}

export interface DeploySubmitPayload {
  ticketId: string;
  automationFlowId: string;
  deploymentId?: string;
  workflowId: string;
  nodes?: DeployNodePayload[];
  deselectedNodeIds?: string[];
  credentials?: { gnbNodeId: string; username: string; password: string }[];
}

export interface DeployApprovePayload {
  ticketId: string;
  automationFlowId: string;
  approve: boolean;
  reason?: string;
}

export interface DeployResponse {
  status: 'accepted';
  ticketId: string;
}

export type DeploymentNodeStatus = 'pending' | 'running' | 'done' | 'failed';

export interface DeploymentNode {
  id: string;
  gnbNodeId: string;
  workflowTicketId: string;
  automationFlowId: string;
  deploymentId: string | null;
  status: DeploymentNodeStatus;
  isNewNode: boolean;
  draftName: string | null;
  errorMessage: string | null;
  logUrl: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  gnbNode?: { nodeName: string } | null;
}

export const gnbDeployService = {
  submit: (payload: DeploySubmitPayload) =>
    api.post<DeployResponse>('/api/gnb-deploy/submit', payload),

  // ── Staged per-node-per-ticket credentials ──
  stageCreds: (payload: {
    gnbNodeId: string;
    workflowTicketId: string;
    ticketId: string;
    automationFlowId: string;
    username: string;
    password: string;
  }) => api.post<{ success: boolean }>('/api/gnb-deploy/creds/staged', payload),

  getStagedCreds: (gnbNodeId: string, workflowTicketId: string, automationFlowId: string) =>
    api.get<{ hasStagedCreds: boolean; username: string | null }>('/api/gnb-deploy/creds/staged', {
      params: { gnbNodeId, workflowTicketId, automationFlowId }
    }),

  deleteStagedCreds: (gnbNodeId: string, workflowTicketId: string, automationFlowId: string) =>
    api.delete<{ success: boolean }>('/api/gnb-deploy/creds/staged', {
      params: { gnbNodeId, workflowTicketId, automationFlowId }
    }),

  approve: (payload: DeployApprovePayload) =>
    api.post<DeployResponse>('/api/gnb-deploy/approve', payload),

  getDeploymentNodes: (workflowId: string, deploymentId?: string, params?: { page?: number; limit?: number; nodeName?: string; draftName?: string; errorMessage?: string; sortBy?: string; sortOrder?: string }) => {
    const qs = new URLSearchParams({ workflowId });
    if (deploymentId) qs.set('deploymentId', deploymentId);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.nodeName) qs.set('nodeName', params.nodeName);
    if (params?.draftName) qs.set('draftName', params.draftName);
    if (params?.errorMessage) qs.set('errorMessage', params.errorMessage);
    if (params?.sortBy) qs.set('sortBy', params.sortBy);
    if (params?.sortOrder) qs.set('sortOrder', params.sortOrder);
    return api.get<{ data: DeploymentNode[]; total: number; page: number; limit: number }>(`/api/gnb-deploy/nodes?${qs.toString()}`);
  },

  getDeploymentSummary: (workflowId: string, deploymentId?: string) => {
    const qs = new URLSearchParams({ workflowId });
    if (deploymentId) qs.set('deploymentId', deploymentId);
    return api.get<{ workflowId: string; deploymentId: string | null; total: number; pending?: number; running?: number; done?: number; failed?: number }>(`/api/gnb-deploy/summary?${qs.toString()}`);
  },
};
